import React from 'react';
import { X } from 'lucide-react';
import { Reminder } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { REMINDER_TYPES } from '../constants';

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSaving: boolean;
  newReminder: Omit<Reminder, 'id' | 'petId' | 'completed'>;
  setNewReminder: (reminder: Omit<Reminder, 'id' | 'petId' | 'completed'>) => void;
  petType: 'chien' | 'chat';
}

export const ReminderModal: React.FC<ReminderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSaving,
  newReminder,
  setNewReminder,
  petType
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-end backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md mx-auto p-6 rounded-t-[2.5rem] animate-slide-up relative shadow-2xl flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-black">{t('app:reminders.add_reminder')}</h3>
          <button onClick={onClose} className="text-gray-300"><X /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:reminders.reminder_type')}</label>
            <select 
              value={newReminder.type} 
              onChange={e => setNewReminder({...newReminder, type: e.target.value as any})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none"
            >
              {REMINDER_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {t(`app:reminder_types.${type.value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:reminders.reminder_date')}</label>
            <input 
              type="date" 
              value={newReminder.date} 
              onChange={e => setNewReminder({...newReminder, date: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
              required 
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:reminders.reminder_title')}</label>
            <input 
              value={newReminder.title} 
              onChange={e => setNewReminder({...newReminder, title: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
              placeholder={t('app:reminders.title_placeholder')} 
            />
          </div>
          <button 
            disabled={isSaving} 
            type="submit" 
            className={`w-full ${petType==='chat'?'bg-orange-600':'bg-indigo-600'} text-white py-4 rounded-[1.5rem] font-black shadow-xl mt-4`}
          >
            {t('common:buttons.save')}
          </button>
        </form>
      </div>
    </div>
  );
};
